import React, { useState, useEffect } from 'react';
import { createClient } from '../../../prismicio';
import { PrismicRichText } from '@prismicio/react';
import Container from 'common/components/Container';
import Image from 'next/image';
import {
  StyledTitle,
  TestimonialItem,
  TestimonialCite,
  AuthorInfo,
  AuthorName,
  AuthorRole,
  AuthorImage,
  TestimonialBlockQuote,
  HeaderTemoignage,
  TestimonialDescription,
  TestimonialsContainer,
  NavButton,
} from './NewCustomerTestimonial.style';

const SectionNewCustomerTestimonial = ({ slice }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 950);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      if (!slice?.items?.length) return;

      const client = createClient();

      const testimonialPromises = slice.items.map(async (item) => {
        if (!item?.content_testimonial?.uid) return null;

        try {
          const testimonialDoc = await client.getByUID(
            'swiftask_testimonial',
            item.content_testimonial.uid,
          );
          return testimonialDoc?.data || null;
        } catch (error) {
          console.error('Error fetching testimonial:', error);
          return null;
        }
      });

      const testimonialData = await Promise.all(testimonialPromises);
      setTestimonials(testimonialData.filter(Boolean));
    };

    fetchTestimonials();
  }, [slice?.items]);

  useEffect(() => {
    if (testimonials.length > 1) {
      const intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1,
        );
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const getVisibleTestimonials = () => {
    if (!testimonials.length) return [];

    const count = isMobile ? 1 : 3;
    const result = [];

    for (let i = 0; i < count; i++) {
      const index = (currentIndex + i) % testimonials.length;
      result.push(testimonials[index]);
    }

    return result;
  };

  if (!slice?.primary) return null;

  const hasMultipleTestimonials = testimonials.length > 3;

  return (
    <Container>
      <HeaderTemoignage>
        <PrismicRichText
          field={slice.primary.title}
          components={{
            paragraph: ({ children }) => (
              <StyledTitle variant="h2">{children}</StyledTitle>
            ),
          }}
        />
        <PrismicRichText
          field={slice.primary.description}
          components={{
            paragraph: ({ children }) => (
              <TestimonialDescription>{children}</TestimonialDescription>
            ),
          }}
        />
      </HeaderTemoignage>

      <TestimonialsContainer>
        {hasMultipleTestimonials && (
          <NavButton className="prev" onClick={prevSlide}>
            &#8249;
          </NavButton>
        )}
        <div className="testimonials-wrapper">
          {getVisibleTestimonials().map((testimonial, index) => (
            <TestimonialItem key={index}>
              <TestimonialCite>
                <AuthorImage>
                  {testimonial?.user_photo?.url && (
                    <Image
                      src={testimonial.user_photo.url}
                      alt={testimonial.user_name || ''}
                      width={50}
                      height={50}
                      style={{ objectFit: 'cover' }}
                    />
                  )}
                </AuthorImage>
                <AuthorInfo>
                  {testimonial?.user_name && (
                    <AuthorName>{testimonial.user_name}</AuthorName>
                  )}
                  {testimonial?.user_role && (
                    <AuthorRole>{testimonial.user_role}</AuthorRole>
                  )}
                </AuthorInfo>
              </TestimonialCite>
              {testimonial?.description && (
                <TestimonialBlockQuote>
                  <PrismicRichText field={testimonial.description} />
                </TestimonialBlockQuote>
              )}
            </TestimonialItem>
          ))}
        </div>
        {hasMultipleTestimonials && (
          <NavButton className="next" onClick={nextSlide}>
            &#8250;
          </NavButton>
        )}
      </TestimonialsContainer>
    </Container>
  );
};

export default SectionNewCustomerTestimonial;
